import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  HStack,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Image,
  Divider,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { RegistrationFormConfigurationDetailForm } from "../../forms/RegistrationFormConfigurationDetailForm";
import { OdmaUrlType } from "../../enums/OdmaUrlType";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { entriesKeyValue } from "../../util/entriesObject";
import { FieldType } from "../../enums/FieldType";
import { VisibleField } from "../../enums/VisibleField";
import { getPaymentProviderLabel } from "../../enums/PaymentProvider";
import { CustomerRiskManagementThirdParty } from "../../enums/CustomerRiskManagementThirdParty";

interface RegistrationFormConfigurationDetailProps {
  create?: boolean;
}

const applyVisibility = (
  fieldName: string,
  spec?: CherryHubAdminApi.FieldSpecification
) => {
  return !(spec?.VisibleFields?.find(
    (field) => field.toLowerCase() == fieldName.toLowerCase()
  )
    ? true
    : false);
};

const formatDateTimeWithoutTimezone = (
  isoString: string | null | undefined
): string => {
  if (!isoString) return "-";

  // Parse the ISO string and extract UTC components
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  // Create a new date with these components in local time (no timezone shift)
  const localDate = new Date(year, month, day, hours, minutes, seconds);

  // Format using the user's locale
  return localDate.toLocaleString();
};

export const RegistrationFormConfigurationDetail = (
  props: RegistrationFormConfigurationDetailProps
) => {
  const [searchParams] = useSearchParams();
  const { businessId, registrationId } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] =
    useState<CherryHubAdminApi.RegistrationFormConfiguration | null>();
  const { data: registrationDoc, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !registrationId) {
        return null;
      }

      return apiClient.getBusinessRegistrationFormConfiguration(
        businessId,
        registrationId
      );
    },
    [businessId, registrationId]
  );
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  useEffect(() => {
    setRegistration(registrationDoc);
  }, [registrationDoc]);

  const onSuccess = useCallback(
    (registration: CherryHubAdminApi.RegistrationFormConfiguration) => {
      setRegistration(registration);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Digital membership registration form created.");
        navigate(
          `/admin/businesses/${businessId}/registration-form-configurations/${registration.Meta?.Id}`
        );
      } else {
        showSuccessToast("Digital membership registration form updated.");
      }
    },
    [showSuccessToast, registration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(
        message ?? "Digital membership registration form update failed."
      );
    },
    [showErrorToast]
  );

  const { data: odmaUrl, isLoading: isOmdaUrlLoading } = useAdminApiRequest(
    (adminApiClient) => {
      if (!businessId || !registrationId) {
        return null;
      }
      return adminApiClient.getOdmaUrl(
        businessId!,
        OdmaUrlType.DigitalMembershipRegistration,
        registrationId!
      );
    },
    [businessId, registrationId]
  );

  interface FieldMatchingConfigurationSectionProps {
    field: {
      Key?: CherryHubAdminApi.MatchableMemberField | undefined;
      Value?: CherryHubAdminApi.FieldMatchingConfiguration | undefined;
    };
    isFallback: boolean;
  }

  const FieldMatchingConfigurationSection = ({
    field,
    isFallback,
  }: FieldMatchingConfigurationSectionProps) => {
    return (
      <VStack w="100%" alignItems="flex-start">
        <ViewControl label="Key" value={field.Key} />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Alternate Member Name Match Field"
          value={field.Value?.AlternateMemberNameMatchField}
        />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Exact Match Score"
          value={field.Value?.ExactMatchScore}
        />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Name Diminuitive Score"
          value={field.Value?.NameDiminuitiveScore}
        />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />
        <ViewControl label="Levenshtein Distance Scores">
          <Table size="sm" flex={3}>
            <Thead>
              <Tr>
                <Th>Key</Th>
                <Th>Value</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(field.Value?.LevenshteinDistanceScores &&
                Object.keys(field.Value?.LevenshteinDistanceScores).length &&
                field.Value?.LevenshteinDistanceScores.map((levenshtein) => (
                  <Tr key={levenshtein.Key}>
                    <Td>{levenshtein.Key}</Td>
                    <Td>{levenshtein.Value}</Td>
                  </Tr>
                ))) || (
                <Tr>
                  <Td colSpan={2} textAlign="center">
                    <i>Levenshtein Distance Scores not set</i>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </ViewControl>

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Name Starts with Percentage of Length"
          value={field.Value?.NameStartsWithPercentageOfLength}
        />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Name Starts with Score"
          value={field.Value?.NameStartsWithScore}
        />

        <Divider
          variant="section"
          marginInlineStart="-1rem !important"
          w="calc(100% + 2rem)"
        />

        <ViewControl
          label="Clear Member Name Field Values"
          value={field.Value?.ClearMemberNameFieldValues?.join(", ") ?? "-"}
        />

        {!isFallback && (
          <>
            <Divider
              variant="section"
              marginInlineStart="-1rem !important"
              w="calc(100% + 2rem)"
            />

            <ViewControl
              label="Test Fallback Match Field when Score Less Than"
              value={field.Value?.TestFallbackMatchFieldWhenScoreLessThan}
            />

            <Divider
              variant="section"
              marginInlineStart="-1rem !important"
              w="calc(100% + 2rem)"
            />
            <ViewControl label="Fallback Match Field">
              <Box flex={3}>
                {(field.Value?.FallbackMatchField && (
                  <FieldMatchingConfigurationSection
                    field={field.Value?.FallbackMatchField}
                    isFallback={true}
                  />
                )) || (
                  <Text textAlign="center">
                    <i>Fallback Match Field object not set</i>
                  </Text>
                )}
              </Box>
            </ViewControl>
          </>
        )}
      </VStack>
    );
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Digital Membership Registration
          Forms
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/registration-form-configurations`}
              >
                Digital Membership Registration Forms
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/registration-form-configurations/${
                  props.create ? "create" : registration?.id
                }`}
              >
                {props.create ? "New" : registration?.FriendlyName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || isOmdaUrlLoading || !registration) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="flex-start" spacing={4}>
                <Box flexGrow={1}>
                  <Skeleton w="320px" height="40px" />
                </Box>
                {!editMode && <Skeleton w="108px" height="40px" />}
                <Skeleton w="92px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <VStack w="100%" spacing={6} alignItems="flex-start">
                <SkeletonText w="50%" mt="4" noOfLines={10} spacing="4" />
                <Skeleton w="100%" height="40px" />
                <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
                <Skeleton w="100%" height="40px" />
                <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
              </VStack>
            </VStack>
          )}

          {!isLoading && !isOmdaUrlLoading && editMode && (
            <RegistrationFormConfigurationDetailForm
              apiClient={apiClient}
              businessId={businessId!}
              registration={registration!}
              onCancel={() =>
                props.create
                  ? navigate(
                      `/admin/businesses/${businessId}/registration-form-configurations`
                    )
                  : setEditMode(false)
              }
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}
          {!isLoading && !isOmdaUrlLoading && !editMode && registration && (
            <>
              <HStack w="100%">
                <Box flexGrow={2} pb={4}>
                  <CopyText text={registration?.Meta?.Id!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {registration?.Meta?.Id} /{" "}
                      {registration?.RegistrationFormConfigurationNo}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{registration?.FriendlyName}</b>
                  </Text>
                </Box>

                <Box>
                  {odmaUrl?.Value != null && (
                    <PreviewButton
                      url={odmaUrl?.Value as string}
                      variant="cherryButton"
                      size="sm"
                    >
                      Open
                    </PreviewButton>
                  )}
                </Box>
                <Box>
                  {odmaUrl?.Value != null && (
                    <CopyText
                      text={odmaUrl?.Value as string}
                      variant="cherryButton"
                      fontSize="sm"
                    >
                      Copy Url
                    </CopyText>
                  )}
                </Box>

                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <HStack w="100%" pb={6}>
                <VStack alignItems="flex-start" w="100%">
                  <ViewControl
                    label="Enabled"
                    value={registration?.IsEnabled ? "Yes" : "No"}
                  />

                  <ViewControl
                    label="Redirect URL"
                    value={registration.RedirectUrl ?? "-"}
                  />

                  <ViewControl
                    label="Integration Id"
                    value={registration?.IntegrationId}
                  />

                  <ViewControl
                    label="Check Code"
                    value={registration?.CheckCode ?? "-"}
                  />

                  <ViewControl
                    label="Language"
                    value={registration?.Language?.LanguageCode ?? "-"}
                  />

                  <ViewControl
                    label="Page Title"
                    value={registration?.PageTitle ?? "-"}
                  />

                  <ViewControl
                    label="Membership Name"
                    value={registration?.MembershipName ?? "-"}
                  />

                  <ViewControl label="Privacy Policy URL">
                    <Box flex={3}>
                      {(registration?.PrivacyPolicyUrl && (
                        <PreviewButton
                          url={`${registration?.PrivacyPolicyUrl}`}
                          adjustMargin
                        >
                          {registration?.PrivacyPolicyUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl label="Terms and Conditions URL">
                    <Box flex={3}>
                      {(registration?.TermsConditionsUrl && (
                        <PreviewButton
                          url={`${registration?.TermsConditionsUrl}`}
                          adjustMargin
                        >
                          {registration?.TermsConditionsUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl
                    label="Time Zone Id"
                    value={registration?.TimeZoneId ?? "-"}
                  />
                </VStack>

                {registration?.LogoFileUrl && (
                  <Box alignSelf="flex-start">
                    <Image
                      maxH="200px"
                      maxW="350px"
                      objectFit="contain"
                      src={registration?.LogoFileUrl}
                    />
                  </Box>
                )}
              </HStack>

              <Accordion
                w="100%"
                allowMultiple
                allowToggle
                defaultIndex={[0, 1, 2, 3, 4, 5, 6]}
                variant="section"
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Form Header and Footer Content</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl label="Header Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html: registration?.HeaderText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Footer Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html: registration?.FooterText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Signee Instructions">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html: registration?.SigneeInstructions ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Signee Acceptance Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  registration?.SigneeAcceptanceText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Successful Submission Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  registration?.SuccessfulSubmissionText ?? "",
                              }}
                            />
                          </ViewControl>
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                {/* <AccordionItem>
                <h2>
                  <AccordionButton
                  >
                    <Box flex="1" textAlign="left">
                      <b>OTP</b>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel width="100%">
                  <Text>
                    <b>OTP Valid Time Period</b> :
                  </Text>
                  <UnorderedList variant="empty" styleType="none">
                    <ListItem ml={4}>
                      <Text>
                        <b>Quantity</b> :{" "}
                        {registration?.OTPValidTimePeriod?.Quantity}
                      </Text>
                    </ListItem>
                    <ListItem ml={4}>
                      <Text>
                        <b>Period</b> :{" "}
                        {registration?.OTPValidTimePeriod?.Period}
                      </Text>
                    </ListItem>
                  </UnorderedList>

                  <Text>
                    <b>OTP Flow Required</b> :{" "}
                    {registration?.OTPFlowRequired ? "Yes" : "No"}
                  </Text>

                  <Text>
                    <b>OTP Valid For Minutes</b> :{" "}
                    {registration?.OTPValidForMinutes}
                  </Text>
                </AccordionPanel>
              </AccordionItem> */}
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Payment</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Payment Required"
                            value={registration?.PaymentRequired ? "Yes" : "No"}
                          />
                          {registration?.PaymentRequired && (
                            <>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Payment Configuration"
                                labelProps={{
                                  fontSize: "sm",
                                  mb: "1",
                                  textDecoration: "underline",
                                }}
                              />
                              {(registration?.PaymentConfiguration && (
                                <>
                                  <ViewControl
                                    label="Payment Provider"
                                    value={getPaymentProviderLabel(
                                      registration?.PaymentConfiguration
                                        ?.PaymentProvider
                                    )}
                                  />
                                  {registration?.PaymentConfiguration
                                    ?.PaymentProvider == "Stripe" && (
                                    <>
                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Stripe Configuration">
                                        {(registration.PaymentConfiguration
                                          .StripeConfiguration && (
                                          <Table flex={3} size="sm">
                                            <Thead>
                                              <Tr>
                                                <Th>Configuration Name</Th>
                                                <Th>Value</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              <Tr>
                                                <Td>Account Id</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.StripeConfiguration
                                                    ?.AccountId ?? "-"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>
                                                  Use On Behalf Of Account Id
                                                </Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.StripeConfiguration
                                                    ?.UseOnBehalfOfAccountId
                                                    ? "Yes"
                                                    : "No"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>
                                                  Use Automatic Payment Method
                                                </Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.StripeConfiguration
                                                    ?.UseAutomaticPaymentMethod
                                                    ? "Yes"
                                                    : "No"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>ISO Currency Code</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.StripeConfiguration
                                                    ?.ISOCurrencyCode ?? "-"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>Order Description</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.StripeConfiguration
                                                    ?.OrderDescription ?? "-"}
                                                </Td>
                                              </Tr>
                                            </Tbody>
                                          </Table>
                                        )) || (
                                          <Text flex={3} fontStyle="italic">
                                            Stripe configuration has not been
                                            set
                                          </Text>
                                        )}
                                      </ViewControl>
                                    </>
                                  )}
                                  {registration?.PaymentConfiguration
                                    ?.PaymentProvider == "Payrix" && (
                                    <>
                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Payrix Configuration">
                                        {(registration.PaymentConfiguration
                                          .PayrixConfiguration && (
                                          <Table flex={3} size="sm">
                                            <Thead>
                                              <Tr>
                                                <Th>Configuration Name</Th>
                                                <Th>Value</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              <Tr>
                                                <Td>Account Id</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.PayrixConfiguration
                                                    ?.AccountId ?? "-"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>
                                                  Use Automatic Payment Method
                                                </Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.PayrixConfiguration
                                                    ?.UseAutomaticPaymentMethod
                                                    ? "Yes"
                                                    : "No"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>Order Description</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.PayrixConfiguration
                                                    ?.OrderDescription ?? "-"}
                                                </Td>
                                              </Tr>
                                            </Tbody>
                                          </Table>
                                        )) || (
                                          <Text flex={3} fontStyle="italic">
                                            Payrix configuration has not been
                                            set
                                          </Text>
                                        )}
                                      </ViewControl>
                                    </>
                                  )}
                                  {registration?.PaymentConfiguration
                                    ?.PaymentProvider == "Worldpay" && (
                                    <>
                                      <Divider
                                        variant="section"
                                        marginInlineStart="-1rem !important"
                                        w="calc(100% + 2rem)"
                                      />

                                      <ViewControl label="Worldpay Configuration">
                                        {(registration.PaymentConfiguration
                                          .WorldpayConfiguration && (
                                          <Table flex={3} size="sm">
                                            <Thead>
                                              <Tr>
                                                <Th>Configuration Name</Th>
                                                <Th>Value</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              <Tr>
                                                <Td>Account Id</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.WorldpayConfiguration
                                                    ?.AccountId ?? "-"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>Order Description</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.WorldpayConfiguration
                                                    ?.OrderDescription ?? "-"}
                                                </Td>
                                              </Tr>
                                              <Tr>
                                                <Td>Template Id</Td>
                                                <Td>
                                                  {registration
                                                    ?.PaymentConfiguration
                                                    ?.WorldpayConfiguration
                                                    ?.TemplateId ?? "-"}
                                                </Td>
                                              </Tr>
                                            </Tbody>
                                          </Table>
                                        )) || (
                                          <Text flex={3} fontStyle="italic">
                                            Worldpay configuration has not been
                                            set
                                          </Text>
                                        )}
                                      </ViewControl>
                                    </>
                                  )}
                                </>
                              )) || (
                                <Text flex={3} fontStyle="italic">
                                  Payment configuration has not been set
                                </Text>
                              )}
                            </>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Two Step Flow</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Two Step Flow Required"
                            value={
                              registration?.TwoStepFlowRequired ? "Yes" : "No"
                            }
                          />

                          {registration?.TwoStepFlowRequired && (
                            <>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />

                              <ViewControl
                                label="Two Step Flow Configuration"
                                labelProps={{
                                  fontSize: "sm",
                                  mb: "1",
                                  textDecoration: "underline",
                                }}
                              />

                              {(registration?.TwoStepFlowConfiguration && (
                                <>
                                  <ViewControl
                                    label="Approved Member Submission Text"
                                    value={
                                      registration?.TwoStepFlowConfiguration
                                        ?.ApprovedMemberSubmissionText ?? "-"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl
                                    label="Rejected Member Submission Text"
                                    value={
                                      registration?.TwoStepFlowConfiguration
                                        ?.RejectedMemberSubmissionText ?? "-"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl
                                    label="Approved Member Pass Update Message"
                                    value={
                                      registration?.TwoStepFlowConfiguration
                                        ?.ApprovedMemberPassUpdateMessage ?? "-"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl
                                    label="Rejected Member Pass Update Message"
                                    value={
                                      registration?.TwoStepFlowConfiguration
                                        ?.RejectedMemberPassUpdateMessage ?? "-"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl label="Membership Request Valid For">
                                    <UnorderedList
                                      variant="empty"
                                      styleType="none"
                                      flex={3}
                                    >
                                      <ListItem ml={4}>
                                        <Text>
                                          <b>Quantity</b>: &nbsp;
                                          {
                                            registration
                                              ?.TwoStepFlowConfiguration
                                              ?.MembershipRequestValidFor
                                              ?.Quantity
                                          }
                                        </Text>
                                      </ListItem>
                                      <ListItem ml={4}>
                                        <Text>
                                          <b>Period</b>
                                          :&nbsp;
                                          {
                                            registration
                                              ?.TwoStepFlowConfiguration
                                              ?.MembershipRequestValidFor
                                              ?.Period
                                          }
                                        </Text>
                                      </ListItem>
                                    </UnorderedList>
                                  </ViewControl>

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl
                                    label="Membership Application Pass"
                                    value={
                                      registration?.TwoStepFlowConfiguration
                                        ?.PassConfigurationId ?? "-"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl label="Membership Application Pass Download Email">
                                    <Table size="sm" flex={3}>
                                      <Thead>
                                        <Tr>
                                          <Th>Configuration Name</Th>
                                          <Th>Value</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {/* <Tr>
                                <Td>Version</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.Version ?? "-"}
                                </Td>
                              </Tr>
                              <Tr>
                                <Td>Landing Page Url Format</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.LandingPageUrlFormat ?? "-"}
                                </Td>
                              </Tr>
                              <Tr>
                                <Td>Landing Page Set Prize Flag</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.LandingPageSetPrizeFlag
                                    ? "Yes"
                                    : "No"}
                                </Td>
                              </Tr>
                              <Tr>
                                <Td>Landing Page Error Url Format</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.LandingPageErrorUrlFormat ?? "-"}
                                </Td>
                              </Tr>
                              <Tr>
                                <Td>Download From App Url Format</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.DownloadFromAppUrlFormat ?? "-"}
                                </Td>
                              </Tr>
                              <Tr>
                                <Td>Download From Invitation Url Format</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.DownloadFromInvitationUrlFormat ?? "-"}
                                </Td>
                              </Tr> */}
                                        <Tr>
                                          <Td>Email Template</Td>
                                          <Td>
                                            {registration
                                              ?.TwoStepFlowConfiguration
                                              ?.PassInvitationHostConfiguration
                                              ?.EmailTemplateId ?? "-"}
                                          </Td>
                                        </Tr>
                                        <Tr>
                                          <Td>Email Sender Name</Td>
                                          <Td>
                                            {registration
                                              ?.TwoStepFlowConfiguration
                                              ?.PassInvitationHostConfiguration
                                              ?.EmailSenderName ?? "-"}
                                          </Td>
                                        </Tr>
                                        {/* <Tr>
                                <Td>Email Sender Email Address</Td>
                                <Td>
                                  {registration?.TwoStepFlowConfiguration
                                    ?.PassInvitationHostConfiguration
                                    ?.EmailSenderEmailAddress ?? "-"}
                                </Td>
                              </Tr> */}
                                      </Tbody>
                                    </Table>
                                  </ViewControl>

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl
                                    label="Send Notification Email to the Venue"
                                    value={
                                      registration?.SendMembershipSubmissionNotificationEmail
                                        ? "Yes"
                                        : "No"
                                    }
                                  />

                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />

                                  <ViewControl label="Membership Application Notification Email">
                                    {(registration?.SendMembershipSubmissionNotificationEmail &&
                                      registration?.SubmissionNotificationEmailConfiguration && (
                                        <Table size="sm" flex={3}>
                                          <Thead>
                                            <Tr>
                                              <Th>Configuration Name</Th>
                                              <Th>Value</Th>
                                            </Tr>
                                          </Thead>
                                          <Tbody>
                                            <Tr>
                                              <Td>Sender Name</Td>
                                              <Td>
                                                {registration
                                                  ?.SubmissionNotificationEmailConfiguration
                                                  ?.SenderName ?? "-"}
                                              </Td>
                                            </Tr>
                                            {/* <Tr>
                          <Td>Sender Email Address</Td>
                          <Td>
                            {
                              registration?.SubmissionNotificationEmailConfiguration
                                ?.SenderEmailAddress
                            }
                          </Td>
                        </Tr> */}
                                            <Tr>
                                              <Td>Email Template</Td>
                                              <Td>
                                                {
                                                  registration
                                                    ?.SubmissionNotificationEmailConfiguration
                                                    ?.TemplateId
                                                }
                                              </Td>
                                            </Tr>
                                            <Tr>
                                              <Td>Recipient Email Addresses</Td>
                                              <Td>
                                                {registration?.SubmissionNotificationEmailConfiguration?.RecipientEmailAddresses?.join(
                                                  ", "
                                                ) ?? "-"}
                                              </Td>
                                            </Tr>
                                          </Tbody>
                                        </Table>
                                      )) || (
                                      <Text flex={3} fontStyle="italic">
                                        Membership Application Notification
                                        Email Configuration has not been set.
                                      </Text>
                                    )}
                                  </ViewControl>
                                </>
                              )) || (
                                <i>
                                  Two step flow configuration has not been set
                                </i>
                              )}
                            </>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Adhoc Pass Invitation Configuration</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          {(registration?.AdhocPassInvitationConfiguration && (
                            <>
                              <ViewControl
                                label="First Device Registration Pass Update
                                          Message"
                                value={
                                  registration?.AdhocPassInvitationConfiguration
                                    ?.FirstDeviceRegistrationPassUpdateMessage ??
                                  "-"
                                }
                              />

                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />

                              <ViewControl
                                label="First Device Registration Pass Update Message
                                Delay"
                              >
                                {(registration.AdhocPassInvitationConfiguration
                                  .FirstDeviceRegistrationPassUpdateMessageDelay && (
                                  <UnorderedList
                                    variant="empty"
                                    styleType="none"
                                    flex={3}
                                  >
                                    <ListItem ml={4}>
                                      <Text>
                                        <b>Quantity</b>:{" "}
                                        {
                                          registration
                                            ?.AdhocPassInvitationConfiguration
                                            .FirstDeviceRegistrationPassUpdateMessageDelay
                                            ?.Quantity
                                        }
                                      </Text>
                                    </ListItem>
                                    <ListItem ml={4}>
                                      <Text>
                                        <b>Period</b>:{" "}
                                        {
                                          registration
                                            ?.AdhocPassInvitationConfiguration
                                            .FirstDeviceRegistrationPassUpdateMessageDelay
                                            ?.Period
                                        }
                                      </Text>
                                    </ListItem>
                                  </UnorderedList>
                                )) || (
                                  <Text flex={3} fontStyle="italic">
                                    First Device Registration Pass Update
                                    Message Delay has not been set
                                  </Text>
                                )}
                              </ViewControl>
                            </>
                          )) || (
                            <Text flex={3} fontStyle="italic">
                              Adhoc pass invitation configuration has not been
                              set
                            </Text>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Notification</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl label="Notification Email Configuration">
                            <Table size="sm" flex={3}>
                              <Thead>
                                <Tr>
                                  <Th>Configuration Name</Th>
                                  <Th>Value</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                <Tr>
                                  <Td>Sender Name</Td>
                                  <Td>
                                    {registration
                                      ?.NotificationEmailConfiguration
                                      ?.SenderName ?? "-"}
                                  </Td>
                                </Tr>
                                {/* <Tr>
                        <Td>Sender Email Address</Td>
                        <Td>
                          {
                            registration?.NotificationEmailConfiguration
                              ?.SenderEmailAddress
                          }
                        </Td>
                      </Tr> */}
                                <Tr>
                                  <Td>Email Template</Td>
                                  <Td>
                                    {
                                      registration
                                        ?.NotificationEmailConfiguration
                                        ?.TemplateId
                                    }
                                  </Td>
                                </Tr>
                                <Tr>
                                  <Td>Recipient Email Addresses</Td>
                                  <Td>
                                    {registration?.NotificationEmailConfiguration?.RecipientEmailAddresses?.join(
                                      ", "
                                    ) ?? "-"}
                                  </Td>
                                </Tr>
                              </Tbody>
                            </Table>
                          </ViewControl>

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Send New Member Notification Email to the Venue"
                            value={
                              registration?.SendNewMemberNotificationEmail
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Send Member Receipt Email"
                            value={
                              registration?.SendMemberReceiptEmail
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Send Member Receipt Email After Membership Creation"
                            value={
                              registration?.SendMemberReceiptEmailAfterMembershipCreation
                                ? "Yes"
                                : "No"
                            }
                          />

                          {registration?.SendMemberReceiptEmail && (
                            <>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />

                              <ViewControl label="Member Receipt Email Configuration">
                                {(registration?.MemberReceiptEmailConfiguration && (
                                  <Table size="sm" flex={3}>
                                    <Thead>
                                      <Tr>
                                        <Th>Configuration Name</Th>
                                        <Th>Value</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      <Tr>
                                        <Td>Sender Name</Td>
                                        <Td>
                                          {registration
                                            ?.MemberReceiptEmailConfiguration
                                            ?.SenderName ?? "-"}
                                        </Td>
                                      </Tr>
                                      <Tr>
                                        <Td>Email Template</Td>
                                        <Td>
                                          {
                                            registration
                                              ?.MemberReceiptEmailConfiguration
                                              ?.TemplateId
                                          }
                                        </Td>
                                      </Tr>
                                      <Tr>
                                        <Td>Recipient Email Addresses</Td>
                                        <Td>
                                          {registration?.MemberReceiptEmailConfiguration?.RecipientEmailAddresses?.join(
                                            ", "
                                          ) ?? "-"}
                                        </Td>
                                      </Tr>
                                    </Tbody>
                                  </Table>
                                )) || (
                                  <Text flex={3} fontStyle="italic">
                                    Member Receipt Email Configuration has not
                                    been set.
                                  </Text>
                                )}
                              </ViewControl>
                            </>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Customer Risk Management Flow</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Flow Enabled"
                            value={
                              registration?.CustomerRiskManagementFlowEnabled
                                ? "Yes"
                                : "No"
                            }
                          />
                          {registration?.CustomerRiskManagementFlowEnabled && (
                            <ViewControl
                              label="Full page redirect"
                              value={
                                registration
                                  ?.CustomerRiskManagementFlowConfiguration
                                  ?.UseFullPageRedirect
                                  ? "Yes"
                                  : "No"
                              }
                            />
                          )}
                          {registration?.CustomerRiskManagementFlowEnabled && (
                            <>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              {(registration?.CustomerRiskManagementFlowConfiguration && (
                                <>
                                  <ViewControl
                                    label="Third Party"
                                    value={
                                      registration
                                        ?.CustomerRiskManagementFlowConfiguration
                                        ?.ThirdParty
                                    }
                                  />
                                  {registration
                                    ?.CustomerRiskManagementFlowConfiguration
                                    ?.ThirdParty ==
                                    CustomerRiskManagementThirdParty.Bndry &&
                                    registration
                                      .CustomerRiskManagementFlowConfiguration
                                      ?.BndryCustomerThirdPartyWorkflowConfiguration && (
                                      <>
                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />
                                        <ViewControl
                                          label="Onboard Individual Entity Job Id"
                                          value={
                                            registration
                                              ?.CustomerRiskManagementFlowConfiguration
                                              ?.BndryCustomerThirdPartyWorkflowConfiguration
                                              ?.OnboardIndividualEntityJobId
                                          }
                                        />
                                      </>
                                    )}
                                  {registration
                                    ?.CustomerRiskManagementFlowConfiguration
                                    ?.ThirdParty ==
                                    CustomerRiskManagementThirdParty.Au10Tix &&
                                    registration
                                      .CustomerRiskManagementFlowConfiguration
                                      ?.Au10TixCustomerThirdPartyWorkflowConfiguration && (
                                      <>
                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />
                                        <ViewControl
                                          label="Workflow Type"
                                          value={
                                            registration
                                              ?.CustomerRiskManagementFlowConfiguration
                                              ?.Au10TixCustomerThirdPartyWorkflowConfiguration
                                              ?.WorkflowType
                                          }
                                        />
                                      </>
                                    )}
                                  <Divider
                                    variant="section"
                                    marginInlineStart="-1rem !important"
                                    w="calc(100% + 2rem)"
                                  />
                                  {(!!registration
                                    .CustomerRiskManagementFlowConfiguration
                                    ?.WorkflowTimeToLive?.Quantity ||
                                    !!registration
                                      .CustomerRiskManagementFlowConfiguration
                                      ?.WorkflowTimeToLive?.Period) && (
                                    <ViewControl
                                      label="Workflow TTL"
                                      value={
                                        registration
                                          ?.CustomerRiskManagementFlowConfiguration
                                          ?.WorkflowTimeToLive?.Quantity +
                                        " " +
                                        registration
                                          ?.CustomerRiskManagementFlowConfiguration
                                          ?.WorkflowTimeToLive?.Period
                                      }
                                    />
                                  )}
                                </>
                              )) || (
                                <Text flex={3} fontStyle="italic">
                                  Customer Risk Management Flow Configuration
                                  has not been set.
                                </Text>
                              )}
                            </>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Custom Form Fields</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <Accordion allowMultiple variant="detail-section">
                          {(registration?.FormFields?.length &&
                            registration.FormFields.map((field, idx) => (
                              <AccordionItem
                                key={
                                  field.FieldSpecification?.SurrogateKey ??
                                  field.Identifier
                                }
                              >
                                <AccordionButton>
                                  <Text>
                                    <b>
                                      {field.Name}
                                      &nbsp;
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          fontWeight: "normal",
                                          color:
                                            "var(--chakra-colors-gray-300)",
                                        }}
                                      >
                                        ({field.Identifier})
                                      </span>
                                    </b>
                                  </Text>
                                  <Text
                                    as="span"
                                    color="red"
                                    fontSize="md"
                                    textAlign="left"
                                    flexGrow={1}
                                  >
                                    {field.FieldSpecification
                                      ?.IsSystemRequired && "*"}
                                  </Text>
                                  <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel width="100%">
                                  <VStack w="100%" alignItems="flex-start">
                                    <ViewControl
                                      label="Name"
                                      value={field.Name}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Intro"
                                      value={field.Intro}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Description"
                                      value={field.Description}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Hide Field Name"
                                      value={field.HideFieldName ? "Yes" : "No"}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Is Multi Select"
                                      value={field.IsMultiSelect ? "Yes" : "No"}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Is Required"
                                      value={field.IsRequired ? "Yes" : "No"}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Is Staff Only"
                                      value={field.IsStaffOnly ? "Yes" : "No"}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Type"
                                      value={field.Type}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Textbox Placeholder Value"
                                      value={field.TextboxPlaceholderValue}
                                    />

                                    {(field.FieldSpecification
                                      ?.IsNonIntegratedField
                                      ? field.Type == FieldType.ListboxGroup
                                      : true) &&
                                      !applyVisibility(
                                        VisibleField.KeyValuePairs,
                                        field.FieldSpecification
                                      ) && (
                                        <>
                                          <Divider
                                            variant="section"
                                            marginInlineStart="-1rem !important"
                                            w="calc(100% + 2rem)"
                                          />

                                          <ViewControl
                                            label="Sort Key Value Pairs By Value"
                                            value={
                                              field.SortKeyValuePairsByValue
                                                ? "Yes"
                                                : "No"
                                            }
                                          />

                                          <Divider
                                            variant="section"
                                            marginInlineStart="-1rem !important"
                                            w="calc(100% + 2rem)"
                                          />

                                          <ViewControl label="Key Value Pairs">
                                            <Table size="sm" flex={3}>
                                              <Thead>
                                                <Tr>
                                                  <Th>Key</Th>
                                                  <Th>Value</Th>
                                                </Tr>
                                              </Thead>
                                              <Tbody>
                                                {(field?.KeyValuePairs &&
                                                  field.KeyValuePairs.length &&
                                                  field.KeyValuePairs.map(
                                                    ({ Key, Value }) => (
                                                      <Tr key={Key}>
                                                        <Td>{Key}</Td>
                                                        <Td>
                                                          {Value as string}
                                                        </Td>
                                                      </Tr>
                                                    )
                                                  )) || (
                                                  <Tr>
                                                    <Td
                                                      colSpan={2}
                                                      textAlign="center"
                                                    >
                                                      <i>
                                                        Key value pairs not set
                                                      </i>
                                                    </Td>
                                                  </Tr>
                                                )}
                                              </Tbody>
                                            </Table>
                                          </ViewControl>
                                        </>
                                      )}

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Default Selected Value"
                                      value={
                                        field.DefaultSelectedValue
                                          ? "Yes"
                                          : "No"
                                      }
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Value"
                                      value={field.Value}
                                    />

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Value Label"
                                      value={field.ValueLabel}
                                    />

                                    {!applyVisibility(
                                      VisibleField.IntegrationObject,
                                      field.FieldSpecification
                                    ) && (
                                      <>
                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl label="Integration Object">
                                          <Table size="sm" flex={3}>
                                            <Thead>
                                              <Tr>
                                                <Th>Key</Th>
                                                <Th>Value</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              {(field?.IntegrationObject &&
                                                Object.keys(
                                                  field.IntegrationObject
                                                ).length &&
                                                entriesKeyValue(
                                                  field.IntegrationObject
                                                ).map(([key, value]) => (
                                                  <Tr key={key}>
                                                    <Td>{key}</Td>
                                                    <Td>
                                                      {typeof value == "object"
                                                        ? JSON.stringify(
                                                            value,
                                                            null,
                                                            2
                                                          )
                                                        : (value as string)}
                                                    </Td>
                                                  </Tr>
                                                ))) || (
                                                <Tr>
                                                  <Td
                                                    colSpan={2}
                                                    textAlign="center"
                                                  >
                                                    <i>
                                                      Integration object not set
                                                    </i>
                                                  </Td>
                                                </Tr>
                                              )}
                                            </Tbody>
                                          </Table>
                                        </ViewControl>
                                      </>
                                    )}

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Unset Value"
                                      value={field.UnsetValue}
                                    />

                                    {field.Type == FieldType.Date && (
                                      <>
                                        {!applyVisibility(
                                          VisibleField.DateSelectedMinRelativeTimePeriod,
                                          field.FieldSpecification
                                        ) && (
                                          <>
                                            <Divider
                                              variant="section"
                                              marginInlineStart="-1rem !important"
                                              w="calc(100% + 2rem)"
                                            />

                                            <ViewControl label="Date Selected Min Relative Time Period">
                                              {(field.DateSelectedMinRelativeTimePeriod && (
                                                <UnorderedList
                                                  variant="empty"
                                                  styleType="none"
                                                  flex={3}
                                                >
                                                  <ListItem ml={4}>
                                                    <Text>
                                                      <b>Quantity</b> :
                                                      {
                                                        field
                                                          .DateSelectedMinRelativeTimePeriod
                                                          ?.Quantity
                                                      }
                                                    </Text>
                                                  </ListItem>
                                                  <ListItem ml={4}>
                                                    <Text>
                                                      <b>Period</b> :
                                                      {
                                                        field
                                                          .DateSelectedMinRelativeTimePeriod
                                                          ?.Period
                                                      }
                                                    </Text>
                                                  </ListItem>
                                                </UnorderedList>
                                              )) || (
                                                <Text
                                                  flex={3}
                                                  fontStyle="italic"
                                                >
                                                  Date Selected Min Relative
                                                  Time Period has not been set
                                                </Text>
                                              )}
                                            </ViewControl>
                                          </>
                                        )}

                                        {!applyVisibility(
                                          VisibleField.DateSelectedMaxRelativeTimePeriod,
                                          field.FieldSpecification
                                        ) && (
                                          <>
                                            <Divider
                                              variant="section"
                                              marginInlineStart="-1rem !important"
                                              w="calc(100% + 2rem)"
                                            />

                                            <ViewControl label="Date Selected Max Relative Time Period">
                                              {(field.DateSelectedMaxRelativeTimePeriod && (
                                                <UnorderedList
                                                  variant="empty"
                                                  styleType="none"
                                                  flex={3}
                                                >
                                                  <ListItem ml={4}>
                                                    <Text>
                                                      <b>Quantity</b> :{" "}
                                                      {
                                                        field
                                                          .DateSelectedMaxRelativeTimePeriod
                                                          ?.Quantity
                                                      }
                                                    </Text>
                                                  </ListItem>
                                                  <ListItem ml={4}>
                                                    <Text>
                                                      <b>Period</b> :{" "}
                                                      {
                                                        field
                                                          .DateSelectedMaxRelativeTimePeriod
                                                          ?.Period
                                                      }
                                                    </Text>
                                                  </ListItem>
                                                </UnorderedList>
                                              )) || (
                                                <Text
                                                  flex={3}
                                                  fontStyle="italic"
                                                >
                                                  Date Selected Max Relative
                                                  Time Period has not been set
                                                </Text>
                                              )}
                                            </ViewControl>
                                          </>
                                        )}

                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Date Selected Min Value"
                                          value={formatDateTimeWithoutTimezone(
                                            field.DateSelectedMinValue
                                          )}
                                        />

                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Date Selected Max Value"
                                          value={formatDateTimeWithoutTimezone(
                                            field.DateSelectedMaxValue
                                          )}
                                        />
                                      </>
                                    )}

                                    {(field.FieldSpecification
                                      ?.IsNonIntegratedField
                                      ? field.Type == FieldType.Checkbox ||
                                        field.Type == FieldType.ListboxGroup
                                      : !applyVisibility(
                                          VisibleField.FeeAmounts,
                                          field.FieldSpecification
                                        )) && (
                                      <>
                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl label="Fee Amounts">
                                          <VStack w="100%" flex={3}>
                                            {(field?.FeeAmounts &&
                                              Object.keys(field.FeeAmounts)
                                                .length &&
                                              entriesKeyValue(
                                                field.FeeAmounts
                                              ).map(([key, value]) => (
                                                <Table size="sm" key={key}>
                                                  <Thead>
                                                    <Tr>
                                                      <Th colSpan={3}>{key}</Th>
                                                    </Tr>
                                                  </Thead>
                                                  <Tbody>
                                                    <Tr>
                                                      <Td
                                                        fontWeight="bold"
                                                        w="0.1%"
                                                        whiteSpace="nowrap"
                                                      >
                                                        Fee Amount
                                                      </Td>
                                                      <Td
                                                        w="0.1%"
                                                        whiteSpace="nowrap"
                                                      >
                                                        :
                                                      </Td>
                                                      <Td>{value.FeeAmount}</Td>
                                                    </Tr>
                                                    <Tr>
                                                      <Td
                                                        fontWeight="bold"
                                                        w="0.1%"
                                                        whiteSpace="nowrap"
                                                      >
                                                        Product Description
                                                      </Td>
                                                      <Td
                                                        w="0.1%"
                                                        whiteSpace="nowrap"
                                                      >
                                                        :
                                                      </Td>
                                                      <Td>
                                                        {
                                                          value.ProductServiceDescription
                                                        }
                                                      </Td>
                                                    </Tr>
                                                  </Tbody>
                                                </Table>
                                              ))) || (
                                              <Text fontStyle="italic" flex={3}>
                                                Fee amounts not set
                                              </Text>
                                            )}
                                          </VStack>
                                        </ViewControl>
                                      </>
                                    )}

                                    {field.Type == FieldType.Textbox && (
                                      <>
                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Field Validation Regex Type"
                                          value={field.FieldValidationRegexType}
                                        />

                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Custom Validation Regex Pattern"
                                          value={
                                            field.CustomValidationRegexPattern
                                          }
                                        />
                                      </>
                                    )}

                                    <Divider
                                      variant="section"
                                      marginInlineStart="-1rem !important"
                                      w="calc(100% + 2rem)"
                                    />

                                    <ViewControl
                                      label="Accept Third Party Data"
                                      value={
                                        field.AcceptThirdPartyData
                                          ? "Yes"
                                          : "No"
                                      }
                                    />

                                    {field.AcceptThirdPartyData &&
                                      field.ThirdPartyDataFieldDataletConfigurations &&
                                      field
                                        .ThirdPartyDataFieldDataletConfigurations
                                        .length > 0 && (
                                        <>
                                          <Divider
                                            variant="section"
                                            marginInlineStart="-1rem !important"
                                            w="calc(100% + 2rem)"
                                          />

                                          <ViewControl label="Third Party Data Configurations">
                                            <VStack
                                              w="100%"
                                              flex={3}
                                              spacing={4}
                                            >
                                              {field.ThirdPartyDataFieldDataletConfigurations.map(
                                                (config, configIdx) => (
                                                  <Table
                                                    size="sm"
                                                    key={configIdx}
                                                  >
                                                    <Thead>
                                                      <Tr>
                                                        <Th colSpan={2}>
                                                          Configuration{" "}
                                                          {configIdx + 1}
                                                        </Th>
                                                      </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Third Party Identifier
                                                        </Td>
                                                        <Td>
                                                          {config.ThirdPartyIdentifier ??
                                                            "-"}
                                                        </Td>
                                                      </Tr>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Json Path
                                                        </Td>
                                                        <Td>
                                                          {config.JsonPath ??
                                                            "-"}
                                                        </Td>
                                                      </Tr>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Override Existing
                                                          Value
                                                        </Td>
                                                        <Td>
                                                          {config.OverrideExistingFieldDataletValue
                                                            ? "Yes"
                                                            : "No"}
                                                        </Td>
                                                      </Tr>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Add to Existing Value
                                                        </Td>
                                                        <Td>
                                                          {config.AddToExistingFieldDataletValue
                                                            ? "Yes"
                                                            : "No"}
                                                        </Td>
                                                      </Tr>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Is Required
                                                        </Td>
                                                        <Td>
                                                          {config.IsRequired
                                                            ? "Yes"
                                                            : "No"}
                                                        </Td>
                                                      </Tr>
                                                      <Tr>
                                                        <Td
                                                          fontWeight="bold"
                                                          w="0.1%"
                                                          whiteSpace="nowrap"
                                                        >
                                                          Read-only for Patron
                                                        </Td>
                                                        <Td>
                                                          {config.IsReadOnlyForPatron
                                                            ? "Yes"
                                                            : "No"}
                                                        </Td>
                                                      </Tr>
                                                    </Tbody>
                                                  </Table>
                                                )
                                              )}
                                            </VStack>
                                          </ViewControl>
                                        </>
                                      )}
                                  </VStack>
                                </AccordionPanel>
                              </AccordionItem>
                            ))) || (
                            <Text fontStyle="italic" flex={3}>
                              Custom form fields not set
                            </Text>
                          )}
                        </Accordion>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>

              <Accordion
                w="100%"
                allowMultiple
                defaultIndex={[0, 1, 2]}
                variant="section"
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Advanced Configuration</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="No Membership Management Service"
                            value={
                              registration?.NoMembershipManagementService
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Promotion Id"
                            value={registration?.PromotionId ?? "-"}
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Promotion Configuration Id"
                            value={
                              registration?.PromotionConfigurationId ?? "-"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Check Existing Member"
                            value={
                              registration?.CheckExistingMember ? "Yes" : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Allow Existing Member Check Override"
                            value={
                              registration?.AllowExistingMemberCheckOverride
                                ? "Yes"
                                : "No"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Existing Member Check Filter Query String"
                            value={
                              registration?.ExistingMemberCheckFilterQueryString ??
                              "-"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Geosuggest Country Filter"
                            value={
                              registration?.GeoSuggestCountryFilter &&
                              registration?.GeoSuggestCountryFilter.length
                                ? registration?.GeoSuggestCountryFilter.join(
                                    ", "
                                  )
                                : "-"
                            }
                          />

                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl
                            label="Use Fuzzy Member Matching"
                            value={
                              registration?.UseFuzzyMemberMatching
                                ? "Yes"
                                : "No"
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                {registration?.UseFuzzyMemberMatching && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Member Matching Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <Accordion allowMultiple variant="detail-section">
                            {(registration?.MemberMatchingConfiguration
                              ?.FieldMatchingConfigurations?.length &&
                              registration.MemberMatchingConfiguration.FieldMatchingConfigurations.map(
                                (field) => (
                                  <AccordionItem key={field.Key}>
                                    <h2>
                                      <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                          <b>
                                            {field.Key}
                                            &nbsp;
                                            <span
                                              style={{
                                                fontSize: "10px",
                                                fontWeight: "normal",
                                                color:
                                                  "var(--chakra-colors-gray-300)",
                                              }}
                                            >
                                              ({field.Key})
                                            </span>
                                          </b>
                                        </Box>
                                        <AccordionIcon />
                                      </AccordionButton>
                                    </h2>
                                    <AccordionPanel width="100%">
                                      <FieldMatchingConfigurationSection
                                        field={field}
                                        isFallback={false}
                                      />
                                    </AccordionPanel>
                                  </AccordionItem>
                                )
                              )) || (
                              <Text fontStyle="italic" flex={3}>
                                Member Matching Configuration not set
                              </Text>
                            )}
                          </Accordion>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Theme Data</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <Box
                          width="100%"
                          borderWidth="1px"
                          borderRadius="lg"
                          boxShadow="lg"
                          overflowX="auto"
                          padding={6}
                          position="relative"
                          minHeight="90px"
                        >
                          <pre
                            style={{
                              fontSize: "var(--chakra-fontSizes-xs)",
                            }}
                          >
                            {JSON.stringify(registration?.ThemeData, null, 2)}
                          </pre>
                        </Box>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
